/**
 * FormSummary Component
 * Displays a summary of all answers for review before submission
 * Allows users to edit any answer by clicking on it
 */

import { Question, FormAnswers } from '../../types/formConfig'
import TurnstileWidget from '../TurnstileWidget'

interface FormSummaryProps {
  questions: Question[]
  answers: FormAnswers
  onEdit: (questionId: string) => void
  onSubmit: () => void
  isSubmitting?: boolean
  turnstileToken?: string
  onTurnstileSuccess?: (token: string) => void
  onTurnstileError?: () => void
  onTurnstileExpire?: () => void
}

const FormSummary = ({
  questions,
  answers,
  onEdit,
  onSubmit,
  isSubmitting = false,
  turnstileToken = '',
  onTurnstileSuccess,
  onTurnstileError,
  onTurnstileExpire
}: FormSummaryProps) => {
  // Format answer for display
  const formatAnswer = (question: Question, answer: string | string[] | number | object): string => {
    if (answer === null || answer === undefined || answer === '') {
      return 'Not answered'
    }

    // For address types, show the full address
    if ((question.questionType === 'address-select' || question.questionType === 'address-manual') && typeof answer === 'object') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Define proper Address type interface
      const addr = answer as any
      return addr.fullAddress || addr.line1 || 'Address provided'
    }

    // For radio and select types, show the label
    if (question.questionType === 'radio' || question.questionType === 'select') {
      const option = question.options?.find(opt => opt.value === String(answer))
      return option?.label || String(answer)
    }

    // For checkbox type, show all selected labels
    if (question.questionType === 'checkbox' && Array.isArray(answer)) {
      if (answer.length === 0) {
        return 'None selected'
      }
      const labels = answer
        .map(val => {
          const option = question.options?.find(opt => opt.value === String(val))
          return option?.label || String(val)
        })
        .filter(Boolean)
      return labels.join(', ')
    }

    // For range type, include unit if available
    if (question.questionType === 'range') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Extend Question type to include unit property
      const unit = (question as any).unit || ''
      return `${answer}${unit}`
    }

    // For all other types, return as string
    return String(answer)
  }

  // Group questions into sections (can be customized)
  const answeredQuestions = questions.filter(q => answers[q.id] !== undefined && answers[q.id] !== '')
  const firstName = String(answers['name'] ?? '').split(' ')[0]
  
  return (
    <div className="w-full max-w-3xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl md:text-4xl font-bold text-dark-plum mb-3">
          {firstName ? `Are these answers correct, ${firstName}?` : 'Are these answers correct?'}
        </h2>
        <p className="text-lg text-gray-600">
          Please review your information below. You can edit any answer by clicking on it.
        </p>
      </div>

      {/* Summary Cards */}
      <div className="space-y-4 mb-8">
        {answeredQuestions.map((question) => {
          const answer = answers[question.id]
          const formattedAnswer = formatAnswer(question, answer)
          const isAnswered = answer !== null && answer !== undefined && answer !== ''

          return (
            <div
              key={question.id}
              onClick={() => onEdit(question.id)}
              className={`
                bg-white border-2 rounded-lg p-6 cursor-pointer transition-all duration-200
                ${isAnswered ? 'border-gray-200 hover:border-primary-green' : 'border-yellow-200 bg-yellow-50'}
                hover:shadow-md
              `}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-grow">
                  {/* Question */}
                  <h3 className="text-sm font-medium text-gray-500 mb-2">
                    {question.questionText}
                  </h3>

                  {/* Answer */}
                  <p className={`text-lg font-semibold ${isAnswered ? 'text-dark-plum' : 'text-yellow-700'}`}>
                    {formattedAnswer}
                  </p>
                </div>

                {/* Edit Icon */}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onEdit(question.id)
                  }}
                  className="flex-shrink-0 p-2 text-gray-400 hover:text-primary-green transition-colors"
                  aria-label={`Edit ${question.questionText}`}
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                    />
                  </svg>
                </button>
              </div>
            </div>
          )
        })}
      </div>

      {/* Security Verification */}
      {onTurnstileSuccess && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-dark-plum mb-4 text-center">
            Security Verification
          </h3>
          <TurnstileWidget
            onSuccess={onTurnstileSuccess}
            onError={onTurnstileError}
            onExpire={onTurnstileExpire}
            className="flex justify-center"
          />
        </div>
      )}

      {/* Submit Button */}
      <div className="text-center">
        <button
          onClick={onSubmit}
          disabled={isSubmitting || (onTurnstileSuccess && !turnstileToken)}
          className={`
            px-12 py-4 rounded-lg font-semibold text-lg text-white transition-all duration-200
            ${
              isSubmitting || (onTurnstileSuccess && !turnstileToken)
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-primary-green hover:bg-primary-green-dark hover:shadow-lg transform hover:scale-105'
            }
          `}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <svg
                className="animate-spin h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Submitting...
            </span>
          ) : (
            'Submit Quote Request'
          )}
        </button>

        {/* Privacy Note */}
        <p className="mt-6 text-sm text-gray-600">
          By submitting this form, you agree to be contacted by Gaia Powered.<br></br>
          Your information will be stored & used in accordance with our privacy policy.<br></br>
        </p>
      </div>
    </div>
  )
}

export default FormSummary
